import { lazy, type ComponentType } from 'react';
import { Route, Routes, useParams } from 'react-router';
import { isDesktopLayout } from '../theme/layout';
import type { OverlayId, TabId } from './destinations';
import { lazyScreen, type Preloadable } from './lazyScreen';
import { expandBackgroundChain, type NavLocation } from './navigator';
import {
  classifyLocation,
  isBaseRoute,
  isGateReachable,
  isStackRoute,
  type BaseRouteClass,
  type GateId,
  type LocationLike,
  type ModalId,
  type PushedPageId,
  type StackRouteClass,
} from './routes';

// Feature entry modules the shell mounts (contract §4). Each loads lazily and
// can be preloaded so tab switches and pushes do not wait on the network.

const home = lazyScreen(() => import('../features/home/HomeScreen'), 'HomeScreen');
const sectionViewAll = lazyScreen(() => import('../features/home/SectionViewAllScreen'), 'SectionViewAllScreen');
const libraryHub = lazyScreen<{ pushedSection?: 'series' | 'authors' }>(() => import('../features/library/LibraryHubScreen'), 'LibraryHubScreen');
const seriesDetail = lazyScreen<{ kind: 'series' | 'collection' }>(() => import('../features/groups/SeriesDetailScreen'), 'SeriesDetailScreen');
const authorDetail = lazyScreen(() => import('../features/groups/AuthorDetailScreen'), 'AuthorDetailScreen');
const bookDetails = lazyScreen(() => import('../features/details/BookDetailsLayer'), 'BookDetailsLayer');
const playerScreen = lazyScreen(() => import('../features/player/PlayerScreen'), 'PlayerScreen');
const miniPlayer = lazyScreen(() => import('../features/player/MiniPlayer'), 'MiniPlayer');
const desktopPlayerBar = lazyScreen(() => import('../features/player/DesktopPlayerBar'), 'DesktopPlayerBar');
const popOutHost = lazyScreen(() => import('../features/player/PopOutHost'), 'PopOutHost');
const statistics = lazyScreen(() => import('../features/stats/StatisticsScreen'), 'StatisticsScreen');
const downloads = lazyScreen<{ pushed?: boolean }>(() => import('../features/downloads/DownloadsScreen'), 'DownloadsScreen');
const readingList = lazyScreen(() => import('../features/readingList/ReadingListScreen'), 'ReadingListScreen');
const menu = lazyScreen(() => import('../features/menu/MenuScreen'), 'MenuScreen');
const settings = lazyScreen(() => import('../features/settings/SettingsScreen'), 'SettingsScreen');
const settingsRoutes = lazyScreen(() => import('../features/settings/SettingsRoutes'), 'SettingsRoutes');
const connections = lazyScreen<{ pushed?: boolean }>(() => import('../features/connections/ConnectionsScreen'), 'ConnectionsScreen');
const connectionsRoutes = lazyScreen(() => import('../features/connections/ConnectionsRoutes'), 'ConnectionsRoutes');
const onboarding = lazyScreen(() => import('../features/onboarding/OnboardingScreen'), 'OnboardingScreen');
const noConnections = lazyScreen(() => import('../features/onboarding/NoConnectionsGate'), 'NoConnectionsGate');
const quickActionHost = lazyScreen(() => import('../features/actions/QuickActionHost'), 'QuickActionHost');

export const MiniPlayer = miniPlayer.Component;
export const DesktopPlayerBar = desktopPlayerBar.Component;
/** Only browsers that can open the pop-out player (Document Picture-in-Picture: desktop Chrome, Edge, Firefox) load its host. */
export const PopOutHost = typeof window !== 'undefined' && 'documentPictureInPicture' in window ? popOutHost.Component : null;
export const QuickActionHost = quickActionHost.Component;
export const DevGallery = import.meta.env.DEV ? lazy(() => import('../dev/Gallery')) : null;

const TABS: Record<TabId, Preloadable> = { home, library: libraryHub, downloads, settings, menu };
const OVERLAYS: Record<OverlayId, Preloadable> = { statistics, 'reading-list': readingList, connections };
const PAGES: Record<PushedPageId, Preloadable> = {
  section: sectionViewAll,
  browse: libraryHub,
  series: seriesDetail,
  collection: seriesDetail,
  author: authorDetail,
  settings: settingsRoutes,
  storageDownloads: downloads,
  generalConnections: connections,
  connections: connectionsRoutes,
  onboarding,
};
const MODALS: Record<ModalId, Preloadable> = { book: bookDetails, player: playerScreen };

export const TAB_SCREENS: Record<TabId, ComponentType> = {
  home: home.Component,
  library: libraryHub.Component,
  downloads: downloads.Component,
  settings: settings.Component,
  menu: menu.Component,
};

export const OVERLAY_SCREENS: Record<OverlayId, ComponentType> = {
  statistics: statistics.Component,
  'reading-list': readingList.Component,
  connections: connections.Component,
};

export const OnboardingScreen = onboarding.Component;
export const NoConnectionsGate = noConnections.Component;

/**
 * Warms the chunks for every tab and the common layers once the app is idle,
 * including the Menu's overlays and the Settings and Connections pages (polish
 * pass): a page whose chunk is still loading slides in blank and fills in
 * after the transition.
 */
export function preloadScreens(): void {
  const all: Preloadable[] = [
    home,
    libraryHub,
    downloads,
    settings,
    menu,
    bookDetails,
    playerScreen,
    seriesDetail,
    authorDetail,
    sectionViewAll,
    statistics,
    readingList,
    connections,
    settingsRoutes,
    connectionsRoutes,
  ];
  all.forEach((s) => void s.preload().catch(() => undefined));
}

export function preloadGates(): void {
  void onboarding.preload().catch(() => undefined);
  void noConnections.preload().catch(() => undefined);
}

/**
 * The screens a sign-in lands on (Home and the docked mini player, or the
 * desktop now-playing bar), warmed while a gate is up (polish pass): the shell
 * then mounts without suspending, instead of a blank pane under the nav bar
 * between the gate and Home.
 */
export function preloadSignInLanding(): void {
  [home, isDesktopLayout() ? desktopPlayerBar : miniPlayer].forEach((s) => void s.preload().catch(() => undefined));
}

/** Runs `task` once the browser is idle (or after 1.2 s without `requestIdleCallback`); returns a cancel. */
export function whenIdle(task: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(task, { timeout: 3000 });
    return () => window.cancelIdleCallback(id);
  }
  const timer = setTimeout(task, 1200);
  return () => clearTimeout(timer);
}

function baseScreen(route: BaseRouteClass): Preloadable {
  return route.kind === 'tab' ? TABS[route.tab] : OVERLAYS[route.overlay];
}

function stackScreen(route: StackRouteClass): Preloadable {
  return route.kind === 'pushed' ? PAGES[route.page] : MODALS[route.modal];
}

export interface EntryContext {
  /** The gate the session asks for, or null when signed in. */
  gate: GateId | null;
  /** A book is loaded (the mini player docks, `/?resume=1` opens the player). */
  hasBook: boolean;
  /** Desktop layout: the now-playing bar instead of the mini player. */
  desktop?: boolean;
}

/**
 * The screens the first frame after startup draws for `location`: the gate,
 * or the layer stack the navigator rebuilds (background chain + top), plus the
 * docked mini player. The startup splash stays until these chunks are loaded.
 */
export function entryScreens(location: LocationLike, { gate, hasBook, desktop = false }: EntryContext): Preloadable[] {
  const signedIn = gate === null;
  let route = classifyLocation(location, { signedIn });
  if (route.kind === 'redirect') route = classifyLocation({ pathname: route.to }, { signedIn });

  if (!signedIn) {
    const gateScreen = gate === 'no-connections' ? noConnections : onboarding;
    return isGateReachable(route) && isStackRoute(route) ? [gateScreen, stackScreen(route)] : [gateScreen];
  }

  const screens: Preloadable[] = [];
  if (isStackRoute(route)) {
    const navLocation: NavLocation = {
      pathname: location.pathname,
      search: location.search ?? '',
      hash: location.hash ?? '',
      state: location.state ?? null,
      key: location.key ?? 'entry',
    };
    const chain = expandBackgroundChain(navLocation, { signedIn });
    screens.push(chain.base ? baseScreen(chain.base.route) : home, ...chain.entries.map((e) => stackScreen(e.route)), stackScreen(route));
  } else if (isBaseRoute(route)) {
    screens.push(baseScreen(route));
    const resume = route.kind === 'tab' && route.tab === 'home' && new URLSearchParams(location.search ?? '').get('resume') === '1';
    if (resume && hasBook) screens.push(playerScreen);
  } else {
    screens.push(home);
  }
  if (hasBook) screens.push(desktop ? desktopPlayerBar : miniPlayer);
  return [...new Set(screens)];
}

function BrowseRoute() {
  const { section } = useParams();
  const Hub = libraryHub.Component;
  return <Hub pushedSection={section === 'authors' ? 'authors' : 'series'} />;
}

const SeriesDetail = seriesDetail.Component;
const SectionViewAll = sectionViewAll.Component;
const AuthorDetail = authorDetail.Component;
const Downloads = downloads.Component;
const Connections = connections.Component;
const SettingsRoutes = settingsRoutes.Component;
const ConnectionsRoutes = connectionsRoutes.Component;
const BookDetails = bookDetails.Component;
const Player = playerScreen.Component;

/** Pushed pages and modal layers, rendered against the entry's own (frozen) location. */
export function StackRoutes({ location }: { location: NavLocation }) {
  return (
    <Routes location={location}>
      <Route path="/home/section/:type" element={<SectionViewAll />} />
      <Route path="/browse/:section" element={<BrowseRoute />} />
      <Route path="/series/:id" element={<SeriesDetail kind="series" />} />
      <Route path="/collection/:id" element={<SeriesDetail kind="collection" />} />
      <Route path="/author/:id" element={<AuthorDetail />} />
      <Route path="/settings/storage/downloads" element={<Downloads pushed />} />
      <Route path="/settings/general/connections" element={<Connections pushed />} />
      <Route path="/settings/*" element={<SettingsRoutes />} />
      <Route path="/connections/*" element={<ConnectionsRoutes />} />
      <Route path="/welcome/*" element={<OnboardingScreen />} />
      <Route path="/book/:id" element={<BookDetails />} />
      <Route path="/item/:id" element={<BookDetails />} />
      <Route path="/player" element={<Player />} />
    </Routes>
  );
}

/** A whole-pane screen (tab, overlay or gate) rendered against its own location. */
export function PaneRoutes({ location, Screen }: { location: NavLocation; Screen: ComponentType }) {
  return (
    <Routes location={location}>
      <Route path="*" element={<Screen />} />
    </Routes>
  );
}
